/**
 * RBM (ReBirth Mod) Export Module
 * -------------------------------------------------------------------------
 * Builds valid ReBirth RB-338 `.rbm` mod files that load in the official
 * ReBirth software, including the proper sample encryption.
 *
 * Container format (reverse-engineered & verified byte-exact against real
 * ModPacker output):
 *
 *   'CAT ' + size(BE u32) + 'PRBM'
 *     HEAD  chunk  (slot 0, fixed 256-byte copyright/version block)
 *     EMBF  chunks (slots 1..N)  -> embedded files
 *     INFO  chunk  (last slot, 1280-byte name/comments/url block)
 *   (all chunks word-aligned: a 0x00 pad byte follows odd-sized data)
 *
 * Each EMBF chunk body = filename (NUL-terminated) + payload.
 *
 * Encryption (content/position dependent add/sub stream cipher):
 *   - A 4096-byte key is derived from the 128MB CD data (RB20FUL.DAT) and the
 *     cleartext prefixes of all EMBF chunks (see deriveKey()).
 *   - The first 0x400 bytes of every entry (name + payload) are cleartext.
 *   - For bytes at/after 0x400:  cipher[i] = (plain[i] - key[bank+pos]) & 0xff
 *     where bank = (slot & 1) ? 0x800 : 0  and the key index cycles with a
 *     slot-dependent period.  (The decoder does the exact inverse: + key.)
 *
 * This module is DOM-free and runs in both the browser and Node (for tests).
 */

// ---------------------------------------------------------------------------
// Fixed HEAD chunk (256 bytes) — identical across every genuine RB-338 mod.
// ---------------------------------------------------------------------------
const HEAD_B64 =
  'WzWzW7wBAAAAKGMpMTk5OCBQcm9wZWxsZXJoZWFkIFNvZnR3YXJlLCBhbGwgcmlnaHRzIHJlc2VydmVk' +
  'LgAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
  'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
  'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' +
  'AAAAAAAAAAAAAAAAAAAAAA==';

function base64ToBytes(b64) {
  if (typeof Buffer !== 'undefined') return new Uint8Array(Buffer.from(b64, 'base64'));
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

const HEAD_BYTES = base64ToBytes(HEAD_B64); // 256 bytes

// ---------------------------------------------------------------------------
// INFO chunk layout (1280 bytes). Fixed field offsets.
// ---------------------------------------------------------------------------
const INFO_SIZE = 0x500;         // 1280
const INFO_COMMENTS_OFF = 0x000; // up to FTP
const INFO_FTP_OFF      = 0x259;
const INFO_WWW_OFF      = 0x322;
const INFO_MODNAME_OFF  = 0x3eb;
const INFO_MARKER_OFF   = 0x431; // two bytes: 0xbc 0x01

// ---------------------------------------------------------------------------
// Small byte helpers
// ---------------------------------------------------------------------------
function writeAscii(buf, off, str, maxLen) {
  const n = maxLen === undefined ? str.length : Math.min(str.length, maxLen);
  for (let i = 0; i < n; i++) buf[off + i] = str.charCodeAt(i) & 0xff;
  return n;
}
function readAscii(b, o, l) {
  let s = '';
  const e = Math.min(b.length, o + l);
  for (let i = o; i < e; i++) s += String.fromCharCode(b[i]);
  return s;
}
function u32BE(v) {
  return [(v >>> 24) & 0xff, (v >>> 16) & 0xff, (v >>> 8) & 0xff, v & 0xff];
}
function readU16BE(b, o) { return ((b[o] << 8) | b[o + 1]) >>> 0; }
function readU32BE(b, o) { return (((b[o] << 24) >>> 0) | (b[o + 1] << 16) | (b[o + 2] << 8) | b[o + 3]) >>> 0; }
function readU16LE(b, o) { return (b[o] | (b[o + 1] << 8)) >>> 0; }
function readU32LE(b, o) { return (b[o] | (b[o + 1] << 8) | (b[o + 2] << 16) | (b[o + 3] << 24)) >>> 0; }

// ---------------------------------------------------------------------------
// Crypto — key derivation + encryption (exact inverse of the decoder).
// ---------------------------------------------------------------------------
function deriveKey(cdBytes, embfDatas) {
  const MOD = 0x8000000 - 0x800;
  const BASE = 0x100;
  if (!cdBytes || cdBytes.length < BASE + MOD + 0x800) {
    throw new Error('CD data (RB20FUL.DAT) missing or too small (expect 134217984 bytes).');
  }
  let s0 = 0x2000 >>> 0;
  let s1 = 0x3000 >>> 0;
  for (const raw of embfDatas) {
    const lim = Math.min(raw.length, 0x400);
    const tmp = new Uint8Array(0x400);
    tmp.fill(0x65);
    tmp.set(raw.slice(0, lim), 0);
    let acc0 = 0 | 0;
    let acc1 = 0 | 0;
    let idx = 0;
    for (let i = 0; i < 0x80; i++) {
      const p = (idx & 0xffff) * 4 % 0x400;
      acc0 = (acc0 + (((tmp[p] << 24) >>> 0) | (tmp[p + 1] << 16) | (tmp[p + 2] << 8) | tmp[p + 3])) | 0;
      idx++;
    }
    for (let i = 0; i < 0x80; i++) {
      const p = (idx & 0xffff) * 4 % 0x400;
      acc1 = (acc1 + (((tmp[p] << 24) >>> 0) | (tmp[p + 1] << 16) | (tmp[p + 2] << 8) | tmp[p + 3])) | 0;
      idx++;
    }
    s0 = (s0 + acc0) >>> 0;
    s1 = (s1 + acc1) >>> 0;
  }
  const off0 = BASE + (s0 % MOD);
  const off1 = BASE + (s1 % MOD);
  const key = new Uint8Array(0x1000);
  key.set(cdBytes.slice(off0, off0 + 0x800), 0);
  key.set(cdBytes.slice(off1, off1 + 0x800), 0x800);
  return key;
}

function encryptPayloadInPlace(payload, nameLen, slot, key4096) {
  if (!(key4096 && key4096.length >= 0x1000)) return;
  if (payload.length <= 0) return;
  const entrySize = payload.length + nameLen;
  if (entrySize <= 0x400) return;
  let start = 0x400 - nameLen;
  if (start < 0) start = 0;
  if (start >= payload.length) return;
  const len = entrySize - 0x400;
  const bank = (slot & 1) ? 0x800 : 0;
  const period = ((slot * 0x11) % 0x785) + 0x7b;
  let pos = 0;
  const max = Math.min(len, payload.length - start);
  for (let i = 0; i < max; i++) {
    payload[start + i] = (payload[start + i] - key4096[bank + pos]) & 0xff;
    pos++;
    if (pos === period) pos = 0;
  }
}

// ---------------------------------------------------------------------------
// AIFF <- WAV conversion.  RB-338 samples are stored as big-endian AIFF PCM.
// ---------------------------------------------------------------------------
function extended80FromDouble(sampleRate) {
  // Encode a double as an IEEE 754 80-bit extended float (big-endian, 10 bytes).
  const out = new Uint8Array(10);
  if (sampleRate <= 0) return out;
  let num = sampleRate;
  let exp = 0;
  // normalise: 1.0 <= mantissa < 2.0
  while (num < 1) { num *= 2; exp--; }
  while (num >= 2) { num /= 2; exp++; }
  const biasedExp = exp + 16383;
  out[0] = (biasedExp >> 8) & 0x7f;
  out[1] = biasedExp & 0xff;
  // 64-bit mantissa with explicit integer bit
  let mant = num; // in [1,2)
  let hi = 0;
  for (let i = 0; i < 32; i++) { hi = (hi * 2) + (mant >= 1 ? 1 : 0); mant = (mant >= 1 ? mant - 1 : mant) * 2; }
  let lo = 0;
  for (let i = 0; i < 32; i++) { lo = (lo * 2) + (mant >= 1 ? 1 : 0); mant = (mant >= 1 ? mant - 1 : mant) * 2; }
  hi = hi >>> 0; lo = lo >>> 0;
  out[2] = (hi >>> 24) & 0xff; out[3] = (hi >>> 16) & 0xff; out[4] = (hi >>> 8) & 0xff; out[5] = hi & 0xff;
  out[6] = (lo >>> 24) & 0xff; out[7] = (lo >>> 16) & 0xff; out[8] = (lo >>> 8) & 0xff; out[9] = lo & 0xff;
  return out;
}

function isWav(bytes) {
  return bytes.length >= 12 && readAscii(bytes, 0, 4) === 'RIFF' && readAscii(bytes, 8, 4) === 'WAVE';
}
function isAiff(bytes) {
  return bytes.length >= 12 && readAscii(bytes, 0, 4) === 'FORM' &&
    (readAscii(bytes, 8, 4) === 'AIFF' || readAscii(bytes, 8, 4) === 'AIFC');
}

function wavToAiff(wavBytes) {
  let off = 12;
  let channels = 1, sampleRate = 44100, bitsPerSample = 16, audioFormat = 1;
  let pcm = null;
  while (off + 8 <= wavBytes.length) {
    const id = readAscii(wavBytes, off, 4);
    const size = readU32LE(wavBytes, off + 4);
    const ds = off + 8;
    if (id === 'fmt ') {
      audioFormat = readU16LE(wavBytes, ds);
      channels = readU16LE(wavBytes, ds + 2);
      sampleRate = readU32LE(wavBytes, ds + 4);
      bitsPerSample = readU16LE(wavBytes, ds + 14);
    } else if (id === 'data') {
      pcm = wavBytes.slice(ds, Math.min(ds + size, wavBytes.length));
    }
    off = ds + size + (size & 1);
  }
  if (!pcm) throw new Error('WAV has no data chunk');
  if (audioFormat !== 1) throw new Error(`unsupported WAV format ${audioFormat} (need PCM)`);
  const bytesPerSample = Math.max(1, Math.ceil(bitsPerSample / 8));
  const numFrames = Math.floor(pcm.length / (channels * bytesPerSample));

  // Convert PCM to AIFF sample encoding:
  //  - 8-bit WAV is unsigned; AIFF is signed 8-bit  -> subtract 128
  //  - >=16-bit is little-endian in WAV; AIFF is big-endian -> byteswap per sample
  let ssnd;
  if (bytesPerSample === 1) {
    ssnd = new Uint8Array(pcm.length);
    for (let i = 0; i < pcm.length; i++) ssnd[i] = (pcm[i] - 128) & 0xff;
  } else {
    ssnd = new Uint8Array(pcm.length - (pcm.length % bytesPerSample));
    for (let i = 0; i + bytesPerSample <= pcm.length; i += bytesPerSample) {
      for (let j = 0; j < bytesPerSample; j++) ssnd[i + j] = pcm[i + (bytesPerSample - 1 - j)];
    }
  }

  // COMM (18) + SSND (8 header + 8 offset/block + data)
  const commSize = 18;
  const ssndSize = 8 + ssnd.length;
  const formSize = 4 /*AIFF*/ + (8 + commSize) + (8 + ssndSize + (ssndSize & 1));
  const out = new Uint8Array(8 + formSize);
  let o = 0;
  writeAscii(out, o, 'FORM'); o += 4;
  out.set(u32BE(formSize), o); o += 4;
  writeAscii(out, o, 'AIFF'); o += 4;
  // COMM
  writeAscii(out, o, 'COMM'); o += 4;
  out.set(u32BE(commSize), o); o += 4;
  out[o++] = (channels >> 8) & 0xff; out[o++] = channels & 0xff;
  out.set(u32BE(numFrames), o); o += 4;
  out[o++] = (bitsPerSample >> 8) & 0xff; out[o++] = bitsPerSample & 0xff;
  out.set(extended80FromDouble(sampleRate), o); o += 10;
  // SSND
  writeAscii(out, o, 'SSND'); o += 4;
  out.set(u32BE(ssndSize), o); o += 4;
  out.set(u32BE(0), o); o += 4; // offset
  out.set(u32BE(0), o); o += 4; // blockSize
  out.set(ssnd, o); o += ssnd.length;
  return out;
}

// Ensure audio payload is AIFF (convert from WAV if needed).
function toAiff(data) {
  if (isAiff(data)) return data;
  if (isWav(data)) return wavToAiff(data);
  // Unknown: assume already an acceptable AIFF-ish payload, pass through.
  return data;
}

// ---------------------------------------------------------------------------
// JPEG: RB-338 stores JPEGs WITHOUT the trailing EOI (FF D9) marker.
// ---------------------------------------------------------------------------
function stripJpegEoi(data) {
  if (data.length >= 2 && data[data.length - 2] === 0xff && data[data.length - 1] === 0xd9) {
    return data.slice(0, data.length - 2);
  }
  return data;
}

// ---------------------------------------------------------------------------
// INFO chunk builder
// ---------------------------------------------------------------------------
function buildInfoChunk({ comments = '', ftp = '', www = '', modName = '' } = {}) {
  const buf = new Uint8Array(INFO_SIZE);
  writeAscii(buf, INFO_COMMENTS_OFF, comments, INFO_FTP_OFF - INFO_COMMENTS_OFF - 1);
  writeAscii(buf, INFO_FTP_OFF, ftp, INFO_WWW_OFF - INFO_FTP_OFF - 1);
  writeAscii(buf, INFO_WWW_OFF, www, INFO_MODNAME_OFF - INFO_WWW_OFF - 1);
  writeAscii(buf, INFO_MODNAME_OFF, modName, INFO_MARKER_OFF - INFO_MODNAME_OFF - 1);
  buf[INFO_MARKER_OFF] = 0xbc;
  buf[INFO_MARKER_OFF + 1] = 0x01;
  return buf;
}

// ---------------------------------------------------------------------------
// Exporter
// ---------------------------------------------------------------------------
export class RBMExporter {
  constructor(options = {}) {
    this.files = [];                 // { name, data, type }
    this.skipEncryption = options.skipEncryption ?? false;
    this.cdBytes = options.cdBytes || null;
    this.modName = options.modName || 'Mod';
    this.texts = {
      comments: options.comments || '',
      ftp: options.ftp || '',
      www: options.www || '',
      modName: options.modName || '',
    };
  }

  /**
   * Add an embedded file.
   * @param {string} name  filename stored in the mod (e.g. "TR808BD.aif")
   * @param {Uint8Array} data  raw bytes (WAV/AIFF for audio, JPEG for image, ...)
   * @param {string} type  'audio' | 'image' | 'rbs' | 'song' | 'custom'
   */
  addFile(name, data, type = 'custom') {
    this.files.push({ name, data: data instanceof Uint8Array ? data : new Uint8Array(data), type });
    return this;
  }

  addFiles(files) { for (const f of files) this.addFile(f.name, f.data, f.type); return this; }
  clear() { this.files = []; return this; }

  setTexts({ comments, ftp, www, modName } = {}) {
    if (comments !== undefined) this.texts.comments = comments;
    if (ftp !== undefined) this.texts.ftp = ftp;
    if (www !== undefined) this.texts.www = www;
    if (modName !== undefined) this.texts.modName = modName;
    return this;
  }

  /**
   * Build the complete .rbm file.
   * @returns {Uint8Array}
   */
  build() {
    // 1) Prepare each EMBF entry body: name + NUL + payload (plaintext).
    const entries = [];
    for (const f of this.files) {
      let payload = f.data;
      const lower = (f.name || '').toLowerCase();
      if (f.type === 'audio' || lower.endsWith('.aif') || lower.endsWith('.aiff') || lower.endsWith('.wav')) {
        payload = toAiff(payload);
      } else if (f.type === 'image' || lower.endsWith('.jpg') || lower.endsWith('.jpeg')) {
        payload = stripJpegEoi(payload);
      }
      const nameBytes = new TextEncoder().encode(f.name);
      const nameLen = nameBytes.length + 1; // + NUL
      const body = new Uint8Array(nameLen + payload.length);
      body.set(nameBytes, 0);
      body[nameBytes.length] = 0;
      body.set(payload, nameLen);
      entries.push({ body, nameLen, payloadOff: nameLen, payloadLen: payload.length });
    }

    // 2) Derive key from cleartext EMBF bodies, then encrypt payload tails.
    if (!this.skipEncryption) {
      const key = deriveKey(this.cdBytes, entries.map((e) => e.body));
      for (let i = 0; i < entries.length; i++) {
        const e = entries[i];
        const slot = i + 1; // HEAD is slot 0
        // encrypt in place on the body's payload region
        const payloadView = e.body.subarray(e.payloadOff);
        encryptPayloadInPlace(payloadView, e.nameLen, slot, key);
      }
    }

    // 3) Build INFO chunk.
    const info = buildInfoChunk({
      comments: this.texts.comments,
      ftp: this.texts.ftp,
      www: this.texts.www,
      modName: this.texts.modName || this.modName,
    });

    // 4) Assemble container chunks: HEAD, EMBF..., INFO.
    const chunks = [];
    chunks.push({ id: 'HEAD', data: HEAD_BYTES });
    for (const e of entries) chunks.push({ id: 'EMBF', data: e.body });
    chunks.push({ id: 'INFO', data: info });

    // Compute total body size (each chunk: 8-byte header + data + optional pad).
    let bodyLen = 0;
    for (const c of chunks) bodyLen += 8 + c.data.length + (c.data.length & 1);

    const total = 8 /* 'CAT '+size */ + 4 /* 'PRBM' */ + bodyLen;
    const out = new Uint8Array(total);
    let o = 0;
    writeAscii(out, o, 'CAT '); o += 4;
    out.set(u32BE(4 + bodyLen), o); o += 4; // size covers 'PRBM' + body
    writeAscii(out, o, 'PRBM'); o += 4;
    for (const c of chunks) {
      writeAscii(out, o, c.id); o += 4;
      out.set(u32BE(c.data.length), o); o += 4;
      out.set(c.data, o); o += c.data.length;
      if (c.data.length & 1) out[o++] = 0; // word-align pad
    }
    return out;
  }

  getCounts() {
    const counts = { audio: 0, image: 0, rbs: 0, song: 0, custom: 0 };
    for (const f of this.files) counts[f.type] = (counts[f.type] || 0) + 1;
    return counts;
  }
  getTotalSize() { return this.files.reduce((s, f) => s + f.data.length, 0); }
}

// ---------------------------------------------------------------------------
// Helpers used by the export UI
// ---------------------------------------------------------------------------
export async function fileToUint8Array(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });
}

export function detectFileType(filename, mimeType = '') {
  const lower = (filename || '').toLowerCase();
  if (mimeType.startsWith('audio/') || lower.endsWith('.aif') || lower.endsWith('.aiff') || lower.endsWith('.wav')) return 'audio';
  if (mimeType.startsWith('image/') || lower.endsWith('.jpg') || lower.endsWith('.jpeg') || lower.endsWith('.bmp') || lower.endsWith('.png')) return 'image';
  if (lower.endsWith('.rbs')) return 'rbs';
  if (mimeType.startsWith('text/') || lower.endsWith('.txt')) return 'text';
  return 'custom';
}

// Exposed for testing / advanced use.
export const _internals = {
  deriveKey, encryptPayloadInPlace, wavToAiff, toAiff, stripJpegEoi,
  buildInfoChunk, HEAD_BYTES, extended80FromDouble,
};
